'use client';
import { Fragment } from 'react';
import { useStore } from '@/store/store';
import { useQuery } from '@tanstack/react-query';
import { getDailyJackpots } from '@/lib/daily-jackpots';
import { calculateTimeLeft, getTime, getTimeLeftText } from '@/utils/daily-jackpots/daily-jackpots-time';
import { JACKPOT_STATE } from '@/utils/constants/daily-jackpots';
import { ValueTicker } from '@/app/elements/value-ticker/value-ticker';
import { CountDownTimer } from '@/app/elements/countdown-timer/countdown-timer';
import { getThemeAndSizeClasses } from '@/utils/daily-jackpots/daily-jackpots-classes';
import { ChasingLights } from '../chasing-lights/chasing-lights';
import { DailyJackpotInfoButton } from '../info-button';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { useGlobalContext } from '@/store/contexts/global-context';

type JackpotAmountAndTimeProps = {
    feedsCode: string;
    theme?: string;
    size?: string; // TODO: revisit theme and size, i don;t think this is fine
    infoButtonLink?: string;
};

export const JackpotAmountAndTime = ({
    feedsCode,
    theme = 'dark',
    size,
    infoButtonLink = ''
}: JackpotAmountAndTimeProps) => {
    const debug = useStore((s) => s.debug);
    const { featureFlags } = useGlobalContext();

    const isDailyJackpotsEnabled = featureFlags[PRODUCT_THROTTLES.DAILY_JACKPOTS];
    const { data = [] } = useQuery({
        queryKey: ['dailyJackpots', debug.mockedState],
        queryFn: () =>
            getDailyJackpots({
                state: debug.mockedState,
                cycle: debug.cycle,
                callback: debug.incrementDailyJackpotsCycle
            }),
        enabled: Boolean(feedsCode && isDailyJackpotsEnabled),
        refetchInterval: 30_000
    });
    const jackpot = data?.find((dailyJackpot) => `${dailyJackpot?.id}` === feedsCode);

    if (!jackpot) {
        return <></>;
    }

    const jackpotAmount = jackpot?.amount;
    const jackpotPrevAmount = jackpot?.prevAmount;
    const isUrgentState = jackpot?.jackpotState === JACKPOT_STATE.URGENT;
    const isJustWonState = jackpot?.jackpotState === JACKPOT_STATE.JUST_WON;
    const { currentTime, hiddenTime, targetTime } = getTime(jackpot?.time);
    const timeLeft = calculateTimeLeft({
        isJustWonState,
        currentTime,
        hiddenTime,
        targetTime
    });
    const timeLeftText = getTimeLeftText(timeLeft);

    const ContainerComponent = !isJustWonState && isUrgentState ? ChasingLights : Fragment;

    return (
        <ContainerComponent>
            <div
                className={getThemeAndSizeClasses({
                    theme,
                    size,
                    isUrgentStateAndNotWon: !isJustWonState && isUrgentState
                })}>
                {!!jackpotAmount && !isJustWonState && (
                    <p className="jackpot-amount-time__value">
                        <ValueTicker prevAmount={jackpotPrevAmount} amount={jackpotAmount} />
                    </p>
                )}
                <span
                    className={`jackpot-amount-time__time${
                        !jackpotAmount || isJustWonState ? ' jackpot-amount-time__time--no-value' : ''
                    }`}>
                    {!isJustWonState && !isUrgentState ? (
                        timeLeftText
                    ) : (
                        <CountDownTimer
                            days={timeLeft?.days}
                            hours={timeLeft?.hours}
                            minutes={timeLeft?.minutes}
                            seconds={timeLeft?.seconds}
                        />
                    )}
                </span>
                <DailyJackpotInfoButton infoButtonLink={infoButtonLink} />
            </div>
        </ContainerComponent>
    );
};
